import React from "react";

/**
 * OfficialCertificateFrame
 * ─────────────────────────
 * Museum-grade wrapper around the certificate image displayed on the public
 * certificate page:
 *   - soft dual shadow → floating feel
 *   - gold-ink micro-border → premium framing
 *   - subtle 1° perspective tilt → magazine-quality depth
 *   - light drag / right-click / selection deterrents
 *
 * The image shown here is the OWNER'S PRE-BAKED SAMPLE artwork
 * (see /app/frontend/src/data/certificateAssets.js — `certificateSample`).
 * The original printable certificate is stored separately and is NEVER
 * referenced from any public route; it is reserved for the future
 * authenticated-owner PDF-download flow.
 *
 * Extensibility hooks (Phase 8+): `stampSlot`, `qrSlot`, `versionSlot` remain
 * available for future serialized/holographic variants (rendered on top-right,
 * bottom-right, and bottom-left respectively). They default to null so today's
 * design is unaffected.
 */
export default function OfficialCertificateFrame({
  src,
  alt,
  stampSlot = null,
  qrSlot = null,
  versionSlot = null,
  className = "",
}) {
  return (
    <div
      className={`relative isolate ${className}`}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      data-testid="official-certificate-frame"
    >
      <div
        className="relative overflow-hidden rounded-[22px] border-[3px] border-[var(--gm-gold)]/50 bg-black/40 backdrop-blur-sm"
        style={{
          boxShadow:
            "0 30px 60px -20px rgba(0,0,0,0.55), 0 12px 24px -12px rgba(212,175,55,0.35), inset 0 0 0 1px rgba(255,255,255,0.05)",
          transform: "perspective(1400px) rotateX(1deg)",
          transformOrigin: "50% 100%",
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="block w-full h-auto select-none pointer-events-none"
          style={{ userSelect: "none", WebkitUserSelect: "none" }}
          data-testid="official-certificate-image"
        />

        {stampSlot && (
          <div
            className="absolute top-3 right-3 pointer-events-none"
            data-testid="official-certificate-stamp-slot"
          >
            {stampSlot}
          </div>
        )}
        {qrSlot && (
          <div
            className="absolute bottom-3 right-3 pointer-events-none"
            data-testid="official-certificate-qr-slot"
          >
            {qrSlot}
          </div>
        )}
        {versionSlot && (
          <div
            className="absolute bottom-3 left-3 pointer-events-none"
            data-testid="official-certificate-version-slot"
          >
            {versionSlot}
          </div>
        )}
      </div>
    </div>
  );
}
